import {
  Agent as BaseAgent,
  type AppBskyActorProfile,
  type AtpAgent,
  type AtprotoServiceType,
  type AtpSessionData,
  type AtpSessionEvent,
  type Did,
  type Un$Typed,
} from '@atproto/api'

import {networkRetry} from '#/lib/async/retry'
import {
  IS_PROD_SERVICE,
  PUBLIC_BSKY_SERVICE,
} from '#/lib/constants'
import {logger} from '#/logger'
import {snoozeBirthdateUpdateAllowedForDid} from '#/state/birthdate'
import {restrictChatSettings} from '#/state/queries/messages/restrictChatSettings'
import {snoozeEmailConfirmationPrompt} from '#/state/shell/reminders'
import {
  prefetchAgeAssuranceServerData,
  setBirthdateForDid,
  setCreatedAtForDid,
} from '#/ageAssurance/data'
import {unsafeGetAndComputeAgeAssurance} from '#/ageAssurance/state'
import {features} from '#/analytics'
import {
  configureModerationForAccount,
  configureModerationForGuest,
} from './moderation'
import {
  agentToSessionAccountOrThrow,
  sessionAccountToSession,
} from './sessionAccount'
import {fetchReauthJwt} from './silentReauth'
import {type SessionAccount} from './types'
import {isSessionExpired, isSessionNearExpiry} from './util'
import {WsocAgent, type WsocialSession} from './wsocAgent'

export {BskyAppAgent} from './bskyAppAgent'
export {
  agentToSessionAccount,
  agentToSessionAccountOrThrow,
  sessionAccountToSession,
} from './sessionAccount'
export {WsocAgent} from './wsocAgent'

export type ProxyHeaderValue = `${Did}#${AtprotoServiceType}`

export function createPublicAgent() {
  configureModerationForGuest() // Side effect but only relevant for tests

  const agent = new WsocAgent({service: PUBLIC_BSKY_SERVICE})
  return agent
}

export async function createAgentAndResume(
  storedAccount: SessionAccount,
  onSessionChange: (
    agent: AtpAgent,
    did: string,
    event: AtpSessionEvent,
  ) => void,
) {
  const agent = new WsocAgent({service: storedAccount.service})
  if (storedAccount.pdsUrl) {
    agent.sessionManager.pdsUrl = new URL(storedAccount.pdsUrl)
  }
  const gates = features.refresh({
    strategy: 'prefer-low-latency',
  })
  const moderation = configureModerationForAccount(agent, storedAccount)
  const prevSession: AtpSessionData = sessionAccountToSession(storedAccount)
  if (isSessionExpired(storedAccount)) {
    await networkRetry(1, () => agent.resumeSession(prevSession))
  } else {
    agent.sessionManager.session = prevSession
  }
  // wsocialVerified is not part of AtpSessionData — restore it explicitly so
  // agentToSessionAccount reads the correct value before the background getSession resolves.
  if (agent.sessionManager.session) {
    ;(agent.sessionManager.session as WsocialSession).wsocialVerified =
      storedAccount.wsocialVerified
  }

  // after session is attached
  const aa = prefetchAgeAssuranceServerData({agent})

  const result = await agent.prepare({
    resolvers: [gates, moderation, aa],
    onSessionChange,
  })

  // Fire background sync AFTER the session handler is wired up so any resulting
  // events ('update' / 'expired') are delivered to onAgentSessionChange.
  if (!storedAccount.signupQueued) {
    if (isSessionNearExpiry(storedAccount)) {
      // Token is past 2/3 of its lifetime — force a real refresh via refreshJwt
      networkRetry(3, () => agent.sessionManager.refreshSession()).catch(
        (e: {status?: unknown, message?: unknown}) => {
          logger.error('networkRetry failed to refresh session', {
            status: e?.status ?? 'unknown',
            safeMessage: e?.message ?? 'unknown',
          })
        },
      )
    } else {
      // Token is still fresh — background sync of server-side session state
      networkRetry(3, () => agent.resumeSession(prevSession)).catch(
        (e: {status?: unknown, message?: unknown}) => {
          logger.error('networkRetry failed to resume session', {
            status: e?.status ?? 'unknown',
            safeMessage: e?.message ?? 'unknown',
          })
        },
      )
    }
  }

  // Preserve reauthJwt from the stored account — not part of the agent session.
  return {
    agent: result.agent,
    account: {...result.account, reauthJwt: storedAccount.reauthJwt},
  }
}

export async function createAgentAndLogin(
  {
    service,
    identifier,
    password,
    authFactorToken,
  }: {
    service: string
    identifier: string
    password: string
    authFactorToken?: string
  },
  onSessionChange: (
    agent: AtpAgent,
    did: string,
    event: AtpSessionEvent,
  ) => void,
) {
  const agent = new WsocAgent({service})
  await agent.login({
    identifier,
    password,
    authFactorToken,
    allowTakendown: true,
  })

  const account = agentToSessionAccountOrThrow(agent)
  const gates = features.refresh({strategy: 'prefer-fresh-gates'})
  const moderation = configureModerationForAccount(agent, account)
  const aa = prefetchAgeAssuranceServerData({agent})

  const result = await agent.prepare({
    resolvers: [gates, moderation, aa],
    onSessionChange,
  })

  // Best-effort fetch of reauthJwt after login.
  const reauthJwt = result.account.accessJwt
    ? await fetchReauthJwt(agent)
    : undefined

  return {
    agent: result.agent,
    account: {...result.account, reauthJwt},
  }
}

export async function createAgentAndCreateAccount(
  {
    service,
    email,
    password,
    handle,
    birthDate,
    inviteCode,
    verificationPhone,
    verificationCode,
    displayName,
  }: {
    service: string
    email: string
    password: string
    handle: string
    birthDate: Date
    inviteCode?: string
    verificationPhone?: string
    verificationCode?: string
    displayName?: string
  },
  onSessionChange: (
    agent: AtpAgent,
    did: string,
    event: AtpSessionEvent,
  ) => void,
) {
  const agent = new WsocAgent({service})
  await agent.createAccount({
    email,
    password,
    handle,
    inviteCode,
    verificationPhone,
    verificationCode,
  })

  // createAccount response does not include email — fetch it explicitly
  // so that agentToSessionAccount captures it and it appears in settings.
  try {
    const {data: sessionData} = await agent.com.atproto.server.getSession()
    if (agent.session && sessionData.email) {
      agent.session.email = sessionData.email
    }
  } catch {
    // non-fatal: email will be populated on next partialRefreshSession call
  }

  const account = agentToSessionAccountOrThrow(agent)
  const gates = features.refresh({strategy: 'prefer-fresh-gates'})
  const moderation = configureModerationForAccount(agent, account)

  const createdAt = new Date().toISOString()
  const birthdate = birthDate.toISOString()

  /*
   * Since we have a race with account creation, profile creation, and AA
   * state, set these values locally to ensure sync reads. Values are written
   * to the server in the next step, so on subsequent reloads, the server will
   * be the source of truth.
   */
  setCreatedAtForDid({did: account.did, createdAt})
  setBirthdateForDid({did: account.did, birthdate})
  snoozeBirthdateUpdateAllowedForDid(account.did)
  // do this last
  const aa = prefetchAgeAssuranceServerData({agent})

  // Not awaited so that we can still get into onboarding.
  // This is OK because we won't let you toggle adult stuff until you set the date.
  if (IS_PROD_SERVICE(service)) {
    void Promise.allSettled([
      networkRetry(3, () => {
        return agent.setPersonalDetails({
          birthDate: birthdate,
        })
      }).catch(e => {
        logger.info(`createAgentAndCreateAccount: failed to set birthDate`)
        throw e
      }),
      networkRetry(3, () => {
        return agent.upsertProfile(prev => {
          const next: Un$Typed<AppBskyActorProfile.Record> = prev || {}
            next.displayName = displayName ? displayName : handle
          next.createdAt = createdAt
          return next
        })
      }).catch(e => {
        logger.info(
          `createAgentAndCreateAccount: failed to set initial profile`,
        )
        throw e
      }),
      // wait for AA data to load first, then check state
      aa.then(() => {
        const {flags} = unsafeGetAndComputeAgeAssurance({did: account.did})
        if (flags?.chatDisabled || flags?.groupChatDisabled) {
          void restrictChatSettings({
            agent,
            restrictIncoming: flags.chatDisabled,
            restrictGroupInvites: flags.groupChatDisabled,
          })
        }
      }),
    ]).then(promises => {
      const rejected = promises.filter(p => p.status === 'rejected')
      if (rejected.length > 0) {
        logger.error(
          `session: createAgentAndCreateAccount failed to save personal details and feeds`,
        )
      }
    })
  } else {
    void Promise.allSettled([
      networkRetry(3, () => {
        return agent.setPersonalDetails({
          birthDate: birthDate.toISOString(),
        })
      }).catch(e => {
        logger.info(`createAgentAndCreateAccount: failed to set birthDate`)
        throw e
      }),
      networkRetry(3, () => {
        return agent.upsertProfile(prev => {
          const next: Un$Typed<AppBskyActorProfile.Record> = prev || {}
          next.createdAt = prev?.createdAt || new Date().toISOString()
            next.displayName = displayName ? displayName : handle
          return next
        })
      }).catch(e => {
        logger.info(
          `createAgentAndCreateAccount: failed to set initial profile`,
        )
        throw e
      }),
    ].filter(Boolean)).then(promises => {
      const rejected = promises.filter(p => p.status === 'rejected')
      if (rejected.length > 0) {
        logger.error(
          `session: createAgentAndCreateAccount failed to save personal details and feeds`,
        )
      }
    })
  }

  try {
    // snooze first prompt after signup, defer to next prompt
    snoozeEmailConfirmationPrompt()
  } catch (e) {
    logger.error(e, {message: `session: failed snoozeEmailConfirmationPrompt`})
  }

  return agent.prepare({
    resolvers: [gates, moderation, aa],
    onSessionChange,
  })
}

export class Agent extends BaseAgent {
  constructor(
    proxyHeader: ProxyHeaderValue | null,
    ...options: ConstructorParameters<typeof BaseAgent>
  ) {
    super(...options)
    if (proxyHeader) {
      this.configureProxy(proxyHeader)
    }
  }
}
